// /src/components/news/WalkingComposer.jsx
// Walking Article Composer — guided step-by-step mobile article creation.
// Steps: Images → Title → Introduction → Story → Tags → Confirm → Post.
// Wave 3: image step added as first step; hero image required.

import React, { useState, useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import {
  ARTICLE_TITLE_MAX,
  ARTICLE_SUMMARY_MAX,
  ARTICLE_CONTENT_MAX,
  TAGS_MAX
} from '../../utils/validationConstants';
import { buildArticleData, insertArticle } from '../../data/articleMutationService';
import { eventService, groupService } from '../../data/dataService';
import TagInput from '../shared/TagInput';
import { processImage, uploadToSupabase } from '../core/ImageCropper/imageProcessing';
import { validateImageFile } from '../../utils/fileValidation';
import { toFormattingLocale } from '../../i18n/resolveLanguage';
import { rateLimitMessage } from '../../utils/rateLimitMessage';
import { isGroupClosingError, GROUP_CLOSING_MESSAGE_KEY } from '../../utils/groupClosingError';

// --- TTS helper ---
function speak(text, locale) {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = toFormattingLocale(locale); // the shared language rule (#85): en → en-GB, others their own code
  utterance.rate = 0.95;
  window.speechSynthesis.speak(utterance);
}

// --- Steps enum ---
const STEPS = ['images', 'title', 'intro', 'story', 'tags', 'confirm'];

/**
 * Compute the best insertion point for an inline image marker in story text.
 * Rules:
 *  - If multiple paragraphs: insert between paragraphs nearest the middle.
 *  - If single block: insert at sentence boundary nearest the middle.
 *  - Never mid-sentence; never at very start or very end.
 * @param {string} text - The story content
 * @returns {number} character index where the marker should be inserted
 */
function computeMarkerInsertionPoint(text) {
  if (!text || !text.trim()) return 0;

  const midpoint = text.length / 2;

  // Try paragraph breaks first (double newline)
  const paraBreakRegex = /\n\s*\n/g;
  let match;
  const breaks = [];
  while ((match = paraBreakRegex.exec(text)) !== null) {
    // Use the end of the break (after whitespace) as insertion point
    breaks.push({ index: match.index + match[0].length, distFromMid: Math.abs(match.index + match[0].length / 2 - midpoint) });
  }

  if (breaks.length > 0) {
    // Filter out breaks at very start or very end (within 5% of edges)
    const minPos = text.length * 0.05;
    const maxPos = text.length * 0.95;
    const validBreaks = breaks.filter(b => b.index > minPos && b.index < maxPos);
    const candidates = validBreaks.length > 0 ? validBreaks : breaks;
    candidates.sort((a, b) => a.distFromMid - b.distFromMid);
    return candidates[0].index;
  }

  // Single block — find sentence boundaries (. ! ? followed by space or end)
  const sentenceEndRegex = /[.!?]\s+/g;
  const sentenceBreaks = [];
  while ((match = sentenceEndRegex.exec(text)) !== null) {
    const pos = match.index + match[0].length;
    sentenceBreaks.push({ index: pos, distFromMid: Math.abs(pos - midpoint) });
  }

  if (sentenceBreaks.length > 0) {
    const minPos = text.length * 0.05;
    const maxPos = text.length * 0.95;
    const validBreaks = sentenceBreaks.filter(b => b.index > minPos && b.index < maxPos);
    const candidates = validBreaks.length > 0 ? validBreaks : sentenceBreaks;
    candidates.sort((a, b) => a.distFromMid - b.distFromMid);
    return candidates[0].index;
  }

  // Fallback: midpoint (shouldn't happen with real content)
  return Math.floor(midpoint);
}

function WalkingComposer({ userId, onBack, selectedArea, localityPublishCoords, groupId = null }) {
  const { t, locale } = useTranslation('walking');
  const { currentUser } = useAuth();
  const theme = useTheme();

  const authorDisplayName = currentUser?.display_name || '';

  // Group mode: anchor to the group's boundary instead of GPS, and skip the GPS gate entirely.
  const isGroupMode = !!groupId;
  const [groupHomeLocalityName, setGroupHomeLocalityName] = useState(null);
  const [groupLocalityResolved, setGroupLocalityResolved] = useState(false);
  // #72: 'located' | 'unlocated' (group has no location: posts into the group only) | 'error'
  const [groupLocalityStatus, setGroupLocalityStatus] = useState(null);
  const [groupIsPrivate, setGroupIsPrivate] = useState(null);

  // Image state — array of { displayUrl, thumbnailUrl, caption, credit }
  const [images, setImages] = useState([]);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [imageError, setImageError] = useState(null);

  // Form data
  const [title, setTitle] = useState('');
  const [intro, setIntro] = useState('');
  const [story, setStory] = useState('');
  const [tags, setTags] = useState([]);

  // Flow state
  const [currentStep, setCurrentStep] = useState(0);
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  const [postSuccess, setPostSuccess] = useState(false);

  // GPS location state — captured on first photo tap, not on mount (avoids iOS permission-prompt block)
  // gpsStatus: 'pending' (not yet requested), 'acquiring' (in progress), 'acquired', 'denied', 'failed'
  const [gpsCoords, setGpsCoords] = useState(null); // { lat, lng }
  const [homeLocalityId, setHomeLocalityId] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('pending');
  const fileInputRef = useRef(null);

  // Group mode: resolve home_locality_id from the group and reflect its privacy — the same
  // lookups the article form uses (groupService.resolveGroupHomeLocality,
  // eventService.getCommunityPrivacy). No GPS involved. Group content follows the group (#72):
  // a multi-boundary group takes its first boundary in boundary_ids order (no picker in the
  // composer); a group with no location posts into the group only, with the notice; a failed
  // lookup blocks posting.
  useEffect(() => {
    if (!isGroupMode) return;
    let cancelled = false;

    groupService.resolveGroupHomeLocality(groupId, { firstOnly: true }).then(({ status, homeLocalityId: resolvedId, boundaryName }) => {
      if (cancelled) return;
      setHomeLocalityId(resolvedId);
      setGroupHomeLocalityName(boundaryName);
      setGroupLocalityStatus(status);
      setGroupLocalityResolved(true);
    });

    eventService.getCommunityPrivacy(groupId).then(isPrivate => {
      if (cancelled || isPrivate === null) return;
      setGroupIsPrivate(isPrivate);
    });

    return () => { cancelled = true; };
  }, [isGroupMode, groupId]);

  // Request GPS + resolve to smallest boundary. Returns a Promise so the caller can await.
  // Called from the "Take photo" button tap (user gesture — safe for iOS/Safari).
  const requestGpsAndResolve = () => new Promise((resolve) => {
    if (!navigator.geolocation) {
      setGpsStatus('denied');
      resolve(false);
      return;
    }

    setGpsStatus('acquiring');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        // Reject vague fixes (> 500m accuracy) — would resolve to the wrong freguesia
        if (accuracy > 500) {
          console.warn(`WalkingComposer: GPS accuracy too low (${Math.round(accuracy)}m), blocking.`);
          setGpsStatus('failed');
          resolve(false);
          return;
        }

        setGpsCoords({ lat: latitude, lng: longitude });

        // Resolve to smallest containing boundary via existing RPC
        try {
          const { data: boundaryId, error } = await supabaseFetch.rpc(
            'resolve_point_to_boundary',
            { p_lat: latitude, p_lng: longitude }
          );
          if (!error && boundaryId) {
            setHomeLocalityId(Number(boundaryId));
          }
        } catch (err) {
          console.warn('WalkingComposer: boundary resolution failed:', err);
        }

        setGpsStatus('acquired');
        resolve(true);
      },
      (error) => {
        console.warn('WalkingComposer: GPS error:', error.code, error.message);
        // PERMISSION_DENIED = 1, POSITION_UNAVAILABLE = 2, TIMEOUT = 3
        setGpsStatus(error.code === 1 ? 'denied' : 'failed');
        resolve(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  });

  // Handle the "Take photo" / "Add photo" button tap.
  // First photo: request GPS → if OK open camera, if not block.
  // Second photo: GPS already acquired, go straight to camera.
  const handleTakePhotoTap = async () => {
    if (images.length >= 2) return;

    if (isGroupMode || gpsStatus === 'acquired') {
      // Group mode: the anchor is the group's boundary, not GPS — open camera directly.
      // Locality mode, second photo: GPS already obtained from the first photo.
      fileInputRef.current?.click();
      return;
    }

    // First photo — request GPS before opening camera
    setImageError(null);
    const ok = await requestGpsAndResolve();
    if (ok) {
      // GPS succeeded — open camera. Note: on iOS, programmatic .click()
      // within a user-gesture-initiated async chain is allowed as long as
      // the original gesture started the chain (the button tap did).
      fileInputRef.current?.click();
    }
    // If GPS failed, gpsStatus is already set to 'denied'/'failed' and
    // the UI shows the error. Camera does not open. No work is lost.
  };

  // --- TTS on step change ---
  useEffect(() => {
    const step = STEPS[currentStep];
    const spokenKeys = {
      images: 'walking.hero.spoken',
      title: 'walking.title.spoken',
      intro: 'walking.intro.spoken',
      story: 'walking.story.spoken',
      tags: 'walking.tags.spoken'
    };
    if (spokenKeys[step]) {
      const timer = setTimeout(() => speak(t(spokenKeys[step]), locale), 300);
      return () => clearTimeout(timer);
    }
  }, [currentStep, t, locale]);

  // --- Counter helpers ---
  const getCounterProps = (value, max) => {
    const remaining = max - value.length;
    const pct = value.length / max;
    let color = '#6b7280';
    if (pct >= 1) color = '#dc2626';
    else if (pct >= 0.9) color = '#d97706';
    return { remaining, color, isOver: remaining < 0 };
  };

  // --- Can advance? ---
  const canAdvance = () => {
    const step = STEPS[currentStep];
    switch (step) {
      case 'images':
        return images.length >= 1 && !imageProcessing; // Hero required, not mid-upload
      case 'title':
        return title.trim().length > 0 && title.length <= ARTICLE_TITLE_MAX;
      case 'intro':
        return intro.trim().length > 0 && intro.length <= ARTICLE_SUMMARY_MAX;
      case 'story':
        return story.trim().length > 0 && story.length <= ARTICLE_CONTENT_MAX;
      case 'tags':
        return true;
      default:
        return true;
    }
  };

  // --- Navigation ---
  const goNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const goBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  // --- Image capture handlers ---
  // Auto-crop: compute a centre-crop rectangle that fills a 4:3 frame from natural dimensions.
  const computeAutoCrop = (naturalWidth, naturalHeight) => {
    const targetAspect = 4 / 3;
    const sourceAspect = naturalWidth / naturalHeight;
    let cropW, cropH, cropX, cropY;
    if (sourceAspect > targetAspect) {
      // Source is wider — crop sides
      cropH = naturalHeight;
      cropW = Math.round(naturalHeight * targetAspect);
      cropX = Math.round((naturalWidth - cropW) / 2);
      cropY = 0;
    } else {
      // Source is taller — crop top/bottom
      cropW = naturalWidth;
      cropH = Math.round(naturalWidth / targetAspect);
      cropX = 0;
      cropY = Math.round((naturalHeight - cropH) / 2);
    }
    return { x: cropX, y: cropY, width: cropW, height: cropH };
  };

  // Load a File into an Image element to read natural dimensions
  const loadImageDimensions = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  const handleFileCapture = async (event) => {
    const file = event.target.files?.[0];
    // Reset the input so the same file can be re-selected
    event.target.value = '';
    if (!file || images.length >= 2) return;

    setImageError(null);
    setImageProcessing(true);

    try {
      // Validate
      const validation = await validateImageFile(file);
      if (!validation.valid) {
        setImageError(validation.error);
        setImageProcessing(false);
        return;
      }

      const uid = userId || currentUser?.id;
      if (!uid) {
        setImageError('You must be logged in to add photos.');
        setImageProcessing(false);
        return;
      }

      // Get natural dimensions and compute auto-crop for 4:3
      const dims = await loadImageDimensions(file);
      const cropArea = computeAutoCrop(dims.width, dims.height);

      // Process (crop + resize + WebP) then upload
      const bucket = images.length === 0 ? 'hero-images' : 'inline-images';
      const { displayBlob, thumbnailBlob, fileName } = await processImage(file, cropArea, '4:3');
      const { displayUrl, thumbnailUrl } = await uploadToSupabase(displayBlob, thumbnailBlob, fileName, bucket, uid);

      setImages(prev => [
        ...prev,
        { displayUrl, thumbnailUrl, caption: '', credit: authorDisplayName }
      ]);
    } catch (err) {
      console.error('Walking image capture error:', err);
      setImageError(err.message || 'Failed to process image.');
    } finally {
      setImageProcessing(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // --- Confirm: swap hero/body ---
  const handleSwapImages = () => {
    if (images.length !== 2) return;
    setImages([images[1], images[0]]);
  };

  // --- Confirm: update caption/credit ---
  const handleImageCaption = (index, caption) => {
    setImages(prev => prev.map((img, i) => i === index ? { ...img, caption } : img));
  };

  const handleImageCredit = (index, credit) => {
    setImages(prev => prev.map((img, i) => i === index ? { ...img, credit } : img));
  };

  // --- Post ---
  const handlePost = async () => {
    if (!title.trim() || !story.trim()) {
      setPostError(t('walking.error.no_text'));
      return;
    }
    if (images.length === 0) {
      setPostError(t('walking.error.no_text'));
      return;
    }
    // Block post if the anchor isn't resolved yet — locality mode needs GPS, group mode needs
    // the group's lookup to have completed without error. A group with no location posts with no
    // anchor, into the group only (#72).
    if (!isGroupMode && (gpsStatus !== 'acquired' || !gpsCoords)) {
      setPostError(t('walking.location.denied'));
      return;
    }
    if (isGroupMode && (!groupLocalityResolved || groupLocalityStatus === 'error')) {
      setPostError(t('walking.error.post_failed'));
      return;
    }

    const uid = userId || currentUser?.id;
    if (!uid) {
      setPostError(t('walking.error.not_logged_in'));
      return;
    }

    setPosting(true);
    setPostError(null);

    // Group mode: the map point is the centroid of the boundary the post is placed in, or none
    // for a group with no location (#72 follow-ups Part 4). Locality mode: the GPS fix.
    const coords = isGroupMode ? await groupService.getBoundaryCentroid(homeLocalityId) : gpsCoords;

    // Hero is always images[0]
    const heroImage = images[0];
    // Walking-mode credit fallback: empty credit → author display name (never "Uncredited")
    const heroCredit = heroImage.credit?.trim() || authorDisplayName;
    const heroCaption = heroImage.caption?.trim() || null;

    // Body image handling
    let finalContent = story.trim();
    let inlineImages = [];

    if (images.length === 2) {
      const bodyImage = images[1];
      const bodyCredit = bodyImage.credit?.trim() || authorDisplayName;
      const bodyCaption = bodyImage.caption?.trim() || '';
      const imageId = `img-${Date.now()}`;

      inlineImages = [{
        id: imageId,
        displayUrl: bodyImage.displayUrl,
        thumbnailUrl: bodyImage.thumbnailUrl,
        position: 0,
        size: 'L',
        caption: bodyCaption,
        credit: bodyCredit
      }];

      // Insert marker at mid-point of story
      const insertPos = computeMarkerInsertionPoint(finalContent);
      const before = finalContent.slice(0, insertPos);
      const after = finalContent.slice(insertPos);
      // Ensure paragraph breaks around the marker
      const prefix = before.endsWith('\n\n') ? '' : '\n\n';
      const suffix = after.startsWith('\n') ? '' : '\n\n';
      finalContent = `${before}${prefix}[IMG:${imageId}]${suffix}${after}`;
    }

    const articleData = buildArticleData({
      title: title.trim(),
      summary: intro.trim() || null,
      content: finalContent,
      tags: tags.length > 0 ? tags : null,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      heroImageDisplayUrl: heroImage.displayUrl,
      heroImageThumbnailUrl: heroImage.thumbnailUrl,
      heroImageAspect: '4:3',
      heroImageCredit: heroCredit,
      imageCaption: heroCaption,
      inlineImages,
      authorId: uid,
      communityId: isGroupMode ? groupId : null,
      homeLocalityId: homeLocalityId,
      isPublished: true
    });

    // is_public: the DB trigger (trg_enforce_article_is_public) is the actual security guard —
    // it derives is_public from community_id on every insert. This just keeps the submitted
    // value from contradicting a known group's privacy; buildArticleData's isPublished stays
    // true regardless, so is_published/published_at are unaffected (this is not a draft toggle).
    if (isGroupMode && groupIsPrivate !== null) {
      articleData.is_public = !groupIsPrivate;
    }

    const { error, isRateLimited, isAuthError } = await insertArticle(articleData);

    if (error) {
      const isSessionExpired = error?.code === 'SESSION_EXPIRED' || error?.message === 'SESSION_EXPIRED';
      if (isSessionExpired) {
        const msg = t('articles:session_expired');
        setPostError(msg);
        window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', {
          detail: { message: msg }
        }));
      } else if (isGroupClosingError(error)) {
        setPostError(t(GROUP_CLOSING_MESSAGE_KEY));
      } else if (isAuthError) {
        setPostError(t('walking.error.not_logged_in'));
      } else if (isRateLimited) {
        // Names the action and the wait (addendum 3 item 8); the old line only if the refusal
        // carries no detail.
        setPostError(rateLimitMessage(error, t) || t('walking.error.rate_limit'));
      } else {
        setPostError(t('walking.error.post_failed'));
      }
      setPosting(false);
      return;
    }

    setPostSuccess(true);
    setPosting(false);

    setTimeout(() => {
      onBack();
    }, 1500);
  };

  // --- Tag handlers ---
  const handleAddTags = (newTags) => {
    setTags(prev => [...prev, ...newTags]);
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  // --- Render helpers ---
  const renderCounter = (value, max) => {
    const { remaining, color, isOver } = getCounterProps(value, max);
    const text = isOver
      ? t('walking.count.over', { n: Math.abs(remaining) })
      : t('walking.count.remaining', { n: remaining });
    return (
      <div style={{ textAlign: 'right', fontSize: '13px', color, fontWeight: isOver ? '600' : '400', marginTop: '4px' }}>
        {text}
      </div>
    );
  };

  // --- Step: Images ---
  const renderStepImages = () => (
    <div style={{ padding: '24px 20px' }}>
      <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
        {t('walking.images.label')}
      </label>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
        {t('walking.images.hint')}
      </p>

      {/* Captured images */}
      {images.map((img, index) => (
        <div key={index} style={{
          position: 'relative',
          marginBottom: '12px',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '2px solid #e5e7eb'
        }}>
          <img
            src={img.displayUrl}
            alt=""
            loading="lazy"
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
          {/* Role badge */}
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            padding: '4px 10px',
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {index === 0 ? t('walking.images.hero_badge') : t('walking.images.body_badge')}
          </div>
          {/* Remove button */}
          <button
            type="button"
            onClick={() => handleRemoveImage(index)}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: 'white',
              border: 'none',
              fontSize: '16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {'\u2715'}
          </button>
        </div>
      ))}

      {/* Hidden file input — triggered programmatically after GPS check */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileCapture}
        style={{ display: 'none' }}
      />

      {/* Add photo button — shown when < 2 images and not processing/acquiring GPS */}
      {images.length < 2 && !imageProcessing && gpsStatus !== 'acquiring' && (
        <button
          type="button"
          onClick={handleTakePhotoTap}
          disabled={gpsStatus === 'denied' || gpsStatus === 'failed'}
          style={{
            width: '100%',
            padding: '40px 20px',
            fontSize: '16px',
            fontWeight: '600',
            backgroundColor: '#f9fafb',
            color: (gpsStatus === 'denied' || gpsStatus === 'failed') ? '#9ca3af' : (theme.primary || '#2563eb'),
            border: '2px dashed #d1d5db',
            borderRadius: '12px',
            cursor: (gpsStatus === 'denied' || gpsStatus === 'failed') ? 'not-allowed' : 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            boxSizing: 'border-box'
          }}
        >
          <span style={{ fontSize: '32px' }}>📷</span>
          <span>{images.length === 0 ? t('walking.hero.button') : t('walking.body.button')}</span>
          {images.length === 1 && (
            <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '400' }}>
              {t('walking.body.skip')}
            </span>
          )}
        </button>
      )}

      {/* Processing spinner */}
      {imageProcessing && (
        <div style={{
          width: '100%',
          padding: '40px 20px',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e5e7eb',
            borderTopColor: theme.primary || '#2563eb',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }} />
          {t('walking.confirm.posting').replace('…', '...')}
        </div>
      )}

      {/* Image error */}
      {imageError && (
        <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px', marginTop: '12px' }}>
          {imageError}
        </div>
      )}

      {/* GPS acquiring spinner — shown while waiting for location before camera opens */}
      {gpsStatus === 'acquiring' && (
        <div style={{
          width: '100%',
          padding: '40px 20px',
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '14px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #e5e7eb',
            borderTopColor: theme.primary || '#2563eb',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }} />
          {t('walking.location.spoken')}
        </div>
      )}
      {(gpsStatus === 'denied' || gpsStatus === 'failed') && (
        <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px', marginTop: '12px' }}>
          {t('walking.location.denied')}
        </div>
      )}
    </div>
  );

  const renderStepTitle = () => (
    <div style={{ padding: '24px 20px' }}>
      {/* Hero image thumbnail as visual anchor */}
      {images.length > 0 && (
        <div style={{ marginBottom: '16px', borderRadius: '8px', overflow: 'hidden' }}>
          <img
            src={images[0].thumbnailUrl || images[0].displayUrl}
            alt=""
            style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
      <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>
        {t('walking.title.label')}
      </label>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('walking.title.placeholder')}
        autoFocus
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '18px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          outline: 'none',
          boxSizing: 'border-box'
        }}
      />
      {renderCounter(title, ARTICLE_TITLE_MAX)}
    </div>
  );

  const renderStepIntro = () => (
    <div style={{ padding: '24px 20px' }}>
      {images.length > 0 && (
        <div style={{ marginBottom: '16px', borderRadius: '8px', overflow: 'hidden' }}>
          <img
            src={images[0].thumbnailUrl || images[0].displayUrl}
            alt=""
            style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
      <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>
        {t('walking.intro.label')}
      </label>
      <textarea
        value={intro}
        onChange={(e) => setIntro(e.target.value)}
        placeholder={t('walking.intro.placeholder')}
        autoFocus
        rows={3}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '16px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          outline: 'none',
          resize: 'vertical',
          minHeight: '100px',
          boxSizing: 'border-box'
        }}
      />
      {renderCounter(intro, ARTICLE_SUMMARY_MAX)}
    </div>
  );

  const renderStepStory = () => (
    <div style={{ padding: '24px 20px' }}>
      {images.length > 0 && (
        <div style={{ marginBottom: '16px', borderRadius: '8px', overflow: 'hidden' }}>
          <img
            src={images[0].thumbnailUrl || images[0].displayUrl}
            alt=""
            style={{ width: '100%', maxHeight: '120px', objectFit: 'cover', display: 'block' }}
          />
        </div>
      )}
      <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '8px' }}>
        {t('walking.story.label')}
      </label>
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
        {t('walking.story.hint')}
      </p>
      <textarea
        value={story}
        onChange={(e) => setStory(e.target.value)}
        placeholder={t('walking.story.placeholder')}
        autoFocus
        rows={8}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '16px',
          border: '2px solid #d1d5db',
          borderRadius: '12px',
          outline: 'none',
          resize: 'vertical',
          minHeight: '200px',
          boxSizing: 'border-box'
        }}
      />
      {renderCounter(story, ARTICLE_CONTENT_MAX)}
    </div>
  );

  const renderStepTags = () => {
    const counterText = t('walking.count.tags', { n: tags.length, max: TAGS_MAX });
    return (
      <div style={{ padding: '24px 20px' }}>
        <label style={{ display: 'block', fontSize: '18px', fontWeight: '700', color: '#1f2937', marginBottom: '12px' }}>
          {t('walking.tags.label')}
        </label>
        <TagInput
          tags={tags}
          onAdd={handleAddTags}
          onRemove={handleRemoveTag}
          max={TAGS_MAX}
          placeholder={t('walking.tags.placeholder')}
          addLabel={t('walking.tags.add')}
          counterText={counterText}
        />
      </div>
    );
  };

  // Anchor not yet resolved — locality mode needs an acquired GPS fix, group mode needs the
  // group's lookup to have completed without error.
  const locationNotReady = isGroupMode
    ? (!groupLocalityResolved || groupLocalityStatus === 'error')
    : (gpsStatus !== 'acquired');

  const renderStepConfirm = () => (
    <div style={{ padding: '24px 20px' }}>
      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1f2937', marginBottom: '20px' }}>
        {t('walking.confirm.heading')}
      </h2>

      {/* Images review */}
      {images.map((img, index) => (
        <div key={index} style={{
          marginBottom: '16px',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Image with role badge */}
          <div style={{ position: 'relative' }}>
            <img
              src={img.displayUrl}
              alt=""
              loading="lazy"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              padding: '4px 10px',
              backgroundColor: index === 0 ? (theme.primary || '#2563eb') : '#6b7280',
              color: 'white',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {index === 0 ? t('walking.images.hero_badge') : t('walking.images.body_badge')}
            </div>
          </div>

          {/* Caption + Credit fields */}
          <div style={{ padding: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
              {t('image.caption.label')}
            </label>
            <input
              type="text"
              value={img.caption}
              onChange={(e) => handleImageCaption(index, e.target.value)}
              placeholder={t('image.caption.placeholder')}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: '8px'
              }}
            />
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
              {t('image.credit.label')}
            </label>
            <input
              type="text"
              value={img.credit}
              onChange={(e) => handleImageCredit(index, e.target.value)}
              placeholder={authorDisplayName}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      ))}

      {/* Swap control — only when 2 images */}
      {images.length === 2 && (
        <button
          type="button"
          onClick={handleSwapImages}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '14px',
            fontWeight: '600',
            backgroundColor: '#f3f4f6',
            color: theme.primary || '#2563eb',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '16px'
          }}
        >
          {t('walking.confirm.hero_toggle')}
        </button>
      )}

      {/* Title */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
          {t('walking.confirm.title_label')}
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '16px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Introduction */}
      {intro && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
            {t('walking.intro.label')}
          </label>
          <textarea
            value={intro}
            onChange={(e) => setIntro(e.target.value)}
            rows={2}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '14px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box'
            }}
          />
        </div>
      )}

      {/* Story */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
          {t('walking.confirm.body_label')}
        </label>
        <textarea
          value={story}
          onChange={(e) => setStory(e.target.value)}
          rows={6}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            outline: 'none',
            resize: 'vertical',
            boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
            {t('walking.tags.label')}
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {tags.map((tag, i) => (
              <span key={i} style={{
                padding: '4px 10px',
                backgroundColor: '#14b8a6',
                color: 'white',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '500'
              }}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Group mode: passive location + privacy line — no picker, the group is already the anchor.
          A failed lookup shows as an error (Post stays disabled). A group with no location shows
          the notice (#72). A private group shows the members-only line instead (it takes
          precedence over the notice) — its posts appear in no locality feed. */}
      {isGroupMode && groupLocalityResolved && groupLocalityStatus === 'error' && (
        <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px', marginBottom: '16px' }}>
          {t('walking.error.post_failed')}
        </div>
      )}
      {isGroupMode && (!groupLocalityResolved || groupLocalityStatus !== 'error') && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '16px' }}>
          <MapPin size={18} style={{ color: theme.primary || '#2563eb', marginTop: '1px', flexShrink: 0 }} />
          <p style={{ fontSize: '14px', color: '#1f2937', margin: 0 }}>
            {groupIsPrivate
              ? t('articles:group_content_members_only')
              : groupLocalityStatus === 'unlocated'
                ? t('articles:group_unlocated_notice')
                : <>
                    {groupHomeLocalityName || t('articles:locality_from_group')}
                    {groupIsPrivate !== null && ` · ${t('groups:visibility_public')}`}
                  </>}
          </p>
        </div>
      )}

      {/* GPS warning on confirm screen */}
      {!isGroupMode && (gpsStatus === 'denied' || gpsStatus === 'failed') && (
        <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px', marginBottom: '16px' }}>
          {t('walking.location.denied')}
        </div>
      )}

      {/* Error / Success */}
      {postError && (
        <div style={{ padding: '12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', fontSize: '14px', marginBottom: '16px' }}>
          {postError}
        </div>
      )}
      {postSuccess && (
        <div style={{ padding: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#16a34a', fontSize: '14px', marginBottom: '16px' }}>
          {t('walking.confirm.success')}
        </div>
      )}

      {/* Post button — disabled until the anchor (GPS fix or group boundary) is resolved */}
      <button
        type="button"
        onClick={handlePost}
        disabled={posting || postSuccess || locationNotReady}
        style={{
          width: '100%',
          padding: '16px',
          fontSize: '18px',
          fontWeight: '700',
          backgroundColor: (posting || postSuccess || locationNotReady) ? '#9ca3af' : (theme.primary || '#2563eb'),
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          cursor: (posting || postSuccess || locationNotReady) ? 'not-allowed' : 'pointer'
        }}
      >
        {posting ? t('walking.confirm.posting') : postSuccess ? t('walking.confirm.success') : t('walking.confirm.post')}
      </button>
    </div>
  );

  // --- Main render ---
  const step = STEPS[currentStep];
  const isConfirm = step === 'confirm';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#ffffff',
      zIndex: 200,
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'auto'
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        flexShrink: 0
      }}>
        <button
          type="button"
          onClick={goBack}
          style={{
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            backgroundColor: 'transparent',
            color: theme.primary || '#2563eb',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          {currentStep === 0 ? '\u2715' : '\u2190'}
        </button>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: '6px' }}>
          {STEPS.map((_, i) => (
            <div
              key={i}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: i === currentStep ? (theme.primary || '#2563eb') : i < currentStep ? '#93c5fd' : '#d1d5db'
              }}
            />
          ))}
        </div>

        {/* Next button (not on confirm step) */}
        {!isConfirm ? (
          <button
            type="button"
            onClick={goNext}
            disabled={!canAdvance()}
            style={{
              padding: '8px 20px',
              fontSize: '14px',
              fontWeight: '700',
              backgroundColor: canAdvance() ? (theme.primary || '#2563eb') : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              cursor: canAdvance() ? 'pointer' : 'not-allowed'
            }}
          >
            {t('walking.nav.next')}
          </button>
        ) : (
          <div style={{ width: '72px' }} />
        )}
      </div>

      {/* Spinner keyframe for image processing */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Step content */}
      <div style={{ flex: 1 }}>
        {step === 'images' && renderStepImages()}
        {step === 'title' && renderStepTitle()}
        {step === 'intro' && renderStepIntro()}
        {step === 'story' && renderStepStory()}
        {step === 'tags' && renderStepTags()}
        {step === 'confirm' && renderStepConfirm()}
      </div>
    </div>
  );
}

export default WalkingComposer;
